"use client"

import { memo, useState, useEffect, useCallback, useMemo } from "react"
import Grid from "@mui/material/Grid2"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import type { PerfilCounts } from "@/app/types/perfil"
import dynamic from "next/dynamic"
import { useClientPerformance } from "@/app/lib/performance/client-performance"

const PerfilCard = dynamic(() => import("./feed-card-optimized"), {
  ssr: true,
  // loading: () => (
  //   <Box
  //     sx={{
  //       height: 450,
  //       bgcolor: "grey.900",
  //       borderRadius: 1,
  //       display: "flex",
  //       alignItems: "center",
  //       justifyContent: "center",
  //     }}
  //   >
  //     Carregando...
  //   </Box>
  // ),
})

interface OptimizedPaginatedGridProps {
  perfils: PerfilCounts[]
}

const INITIAL_LOAD = 12 // Load first 12 profiles
const LOAD_MORE_COUNT = 8 // Load 8 more each time

const OptimizedPaginatedGrid = memo(function OptimizedPaginatedGrid({ perfils }: OptimizedPaginatedGridProps) {
  useClientPerformance("OptimizedPaginatedGrid")

  const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD)
  const [isLoading, setIsLoading] = useState(false)

  const visiblePerfils = useMemo(() => perfils.slice(0, visibleCount), [perfils, visibleCount])

  const hasMore = visibleCount < perfils.length

  const loadMore = useCallback(() => {
    setIsLoading(true)

    // Simulate loading delay for better UX
    setTimeout(() => {
      setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, perfils.length))
      setIsLoading(false)
    }, 300)
  }, [perfils.length])

  // Auto-load more when scrolling near bottom
  useEffect(() => {
    const handleScroll = () => {
      if (isLoading || !hasMore) return

      const scrollTop = window.pageYOffset
      const windowHeight = window.innerHeight
      const docHeight = document.documentElement.offsetHeight

      if (scrollTop + windowHeight >= docHeight - 1000) {
        loadMore()
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [loadMore, isLoading, hasMore])

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {visiblePerfils.map((perfil, index) => (
          <Grid
            key={perfil.slug}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
              lg: 3,
              xl: 3,
            }}
          >
            <PerfilCard
              perfil={perfil}
              priority={index < 4} // Only prioritize first 4 images
            />
          </Grid>
        ))}
      </Grid>

      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <Button variant="outlined" onClick={loadMore} disabled={isLoading} sx={{ minWidth: 200 }}>
            {isLoading ? "Carregando..." : `Carregar mais (${perfils.length - visibleCount} restantes)`}
          </Button>
        </Box>
      )}

      {!hasMore && perfils.length > INITIAL_LOAD && (
        <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
          Todos os {perfils.length} perfis foram carregados
        </Box>
      )}
    </>
  )
})

export default OptimizedPaginatedGrid
